import {
  useState,
  createContext,
  useContext,
  useMemo,
  useEffect,
  PropsWithChildren,
} from 'react';
import { QBLoader } from 'components';
import { useLocale, useLoginCountry, usePersistentStorage } from 'hooks';
import { getExpirationDate } from 'hooks/useCookies';
import { type Locale } from 'types';

export type SessionData = {
  userId: string;
  firstOnboardingCompleted: boolean;
  email?: string;
  fullName?: string;
  expires?: number;
} | null;

type SessionProviderValue =
  | (SessionData & {
      setSession: (sessionData: SessionData) => void;
    })
  | {
      setSession: (sessionData: SessionData) => void;
    };

export const SESSION_STORE_NAME = 'qb_app_session';
export const OLD_SESSION_STORE_NAME = 'qb_web_session';

const countryLocale: Record<string, Locale> = {
  SE: 'sv',
  NO: 'nn',
};

const SessionContext = createContext<SessionProviderValue>({
  setSession: () => undefined,
});

export const useSetSession = () => {
  const { setValue: setSessionData } =
    usePersistentStorage<SessionData>(SESSION_STORE_NAME);
  const { setSession } = useContext(SessionContext);
  const { changeLocale } = useLocale();
  const { loginCountry } = useLoginCountry();
  return async (sessionData: SessionData, tokenTtlInSeconds?: number) => {
    if (loginCountry in countryLocale) {
      changeLocale(countryLocale[loginCountry]);
    } else {
      changeLocale('en-US');
    }

    const expires = tokenTtlInSeconds
      ? getExpirationDate(tokenTtlInSeconds).getTime()
      : sessionData?.expires ?? getExpirationDate(30 * 24 * 60 * 60).getTime();

    await setSessionData(sessionData ? { ...sessionData, expires } : null);
    setSession(sessionData);
  };
};

export const useClearSession = () => {
  const { setSession } = useContext(SessionContext);
  const { removeValue: removeSessionData } =
    usePersistentStorage<SessionData>(SESSION_STORE_NAME);

  return async () => {
    await removeSessionData();
    sessionStorage.clear();
    setSession(null);
  };
};

export const useSession: () => SessionData = () => {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within SessionProvider');
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { setSession, ...rest } = context;

  return Object.keys(rest).length > 0 ? (rest as SessionData) : null;
};

export const SessionProvider = ({ children }: PropsWithChildren) => {
  // Remove this at some point, it's for migration purposes to make sure users stay logged in
  const oldSession = localStorage.getItem(OLD_SESSION_STORE_NAME);
  if (oldSession) {
    localStorage.removeItem(OLD_SESSION_STORE_NAME);
  }
  const {
    value: sessionData,
    removeValue: removeSessionData,
    loading,
  } = usePersistentStorage<SessionData>(
    SESSION_STORE_NAME,
    oldSession ? (JSON.parse(oldSession) as SessionData) : undefined
  );
  const [session, setSession] = useState<SessionData>();

  useEffect(() => {
    if (!session && sessionData) {
      const expiresDate =
        sessionData && sessionData.expires
          ? new Date(sessionData.expires)
          : getExpirationDate(30 * 24 * 60 * 60);

      if (expiresDate > new Date()) {
        setSession(sessionData);
      } else {
        void removeSessionData();
      }
    }
  }, [sessionData, session, removeSessionData]);

  const value = useMemo(() => ({ ...session, setSession }), [session]);

  if (loading) return <QBLoader />;
  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
};
